import assign from 'lodash/assign';

import { DataServiceModule } from 'services/data/data.service';
import { StoreInformationServiceModule } from 'services/data/storeInformation.service';
import { AppSettingsModule } from 'services/settings.provider';
import { ExpireCacheServiceModule } from 'services/utilities/expireCache.service';

export const StoreSettingsServiceModule = angular
    .module('app.services.data.storeSettings', [
        DataServiceModule,
        StoreInformationServiceModule,
        AppSettingsModule,
        ExpireCacheServiceModule
    ]).service('storeSettingsService', (
        $q,
        dataService,
        storeInformationService,
        settings,
        expireCache
    ) => {
        return {
            tabs: {
                STOREINFO: 0,
                PAYMENT: 1,
                SHIPPING: 2,
                SEO: 3
            },
            get,
            save,
            getShippingMethods,
            getShippingMethodsOrDefault,
            getStoreSettings,
            isPlaceholderShipping,
            createDefaultShippingMethods
        };

        function get(force = false) {
            return $q.all([storeInformationService.getStore(force), getShippingMethods()]).then((response) => {
                return {
                    storeInformation: response[0],
                    shippingMethods: response[1]
                };
            });
        }

        function save(storeInformation, shippingMethods) {
            return $q.all([storeInformationService.save(storeInformation), saveShippingMethods(shippingMethods)])
                .then(expireCache)
                .then((response) => {
                    return {
                        storeInformation: response[0],
                        shippingMethods: response[1]
                    };
                })
                .catch((error) => {
                    if (isConflictError(error)) {
                        const force = true;
                        return this.get(force)
                            .then((response) => {
                                if (response.storeInformation.emailVerified && !storeInformation.emailVerified) {
                                    storeInformation.emailVerified = true;
                                    storeInformation.revision = response.storeInformation.revision;
                                    shippingMethods.revision = response.shippingMethods.revision;
                                    return this.save(storeInformation, shippingMethods);
                                }
                                return $q.reject({ onCatch: 'resetModel' });
                            });
                    }
                    if (isStoreUrlInUseError(error)) {
                        return $q.reject({
                            onCatch: 'showStoreUrlInUseError',
                            message: error.data.message
                        });
                    }
                    return $q.reject(error);
                });
        }

        function isConflictError(error) {
            return (error.data && error.data.statusCode === 409) || error.status === 409;
        }

        function isStoreUrlInUseError(error) {
            return error.data
                    && error.data.statusCode === 400
                    && error.data.message.indexOf('is being used by another store') >= 0;
        }

        function getStoreSettings() {
            return dataService.get(settings.api.storeSettings);
        }

        function getShippingMethods(clearCache) {
            let endpoint = settings.api.shippingMethods;
            if (clearCache) endpoint = `${endpoint}?${(new Date()).getTime()}`;
            return getShippingMethodsFromEndpoint(endpoint);
        }

        function getShippingMethodsOrDefault() {
            const force = true;
            return this.getShippingMethods(force).then((response) => {
                if (response === null) {
                    return storeInformationService.getStore(force)
                        .then((storeInformation) => {
                            return this.save(
                                storeInformation,
                                this.createDefaultShippingMethods()
                            );
                        })
                        .then((data) => {
                            return data.shippingMethods;
                        });
                }
                if (this.isPlaceholderShipping(response)) {
                    return this.createDefaultShippingMethods(response);
                }
                return response;
            });
        }

        function getShippingMethodsFromEndpoint(endpoint) {
            return dataService.get(endpoint)
                .then((response) => {
                    if (response.items && response.items[0]) return response.items[0]; // old endpoint
                    return response;
                })
                .catch(() => {
                    return null;
                });
        }

        function saveShippingMethods(shippingMethods) {
            return saveShippingMethodsToEndpoint(settings.api.shippingMethods, shippingMethods);
        }

        function saveShippingMethodsToEndpoint(endpoint, shippingMethods) {
            if (shippingMethods.id) {
                return dataService.save(endpoint, null, shippingMethods);
            }
            return dataService.create(endpoint, shippingMethods);
        }

        function isPlaceholderShipping(shippingMethods) {
            if (shippingMethods === null) return false;
            if (shippingMethods.shippingBand) return false; // Legacy shipping, will be migrated by settings detail
            if (!shippingMethods.shippingBands) return false;

            const bands = shippingMethods.shippingBands;
            return !(bands.byPrice.enabled || bands.byWeight.enabled || bands.free.enabled);
        }

        function createDefaultShippingMethods(shippingMethods = {}) {
            return assign(shippingMethods, {
                shippingBands: {
                    byPrice: {
                        enabled: false,
                        bands: []
                    },
                    byWeight: {
                        enabled: false,
                        bands: []
                    },
                    free: {
                        enabled: true,
                        deliveryEstimate: '',
                        priceThreshold: 0
                    }
                }
            });
        }
    }).name;



// WEBPACK FOOTER //
// ./src/app/services/data/storeSettings.service.js